import React from 'react';
import styled from 'styled-components';
import axios from 'axios';
import moment from 'moment';
import { useParams, useHistory } from 'react-router-dom';
import printJS from 'print-js';
import { EnhancedAppointment } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { message, Spin, ModalComponents } from 'components';
import { AuthContext } from 'contexts';
import { isAuthForRole } from 'utils';
import { ListingType, DocumentType, AddHistoryEntryType } from './types';

type ListingDocumentsDTO = {
  documents: DocumentType[];
  listing: ListingType;
};

type TabType = 'all' | 'archived';

type DocumentsContextType = {
  loading: boolean;
  currentTab: TabType;
  setCurrentTab: React.Dispatch<React.SetStateAction<TabType>>;
  documents: DocumentType[];
  documentsToView: DocumentType[];
  listing: ListingType;
  listingAgreement: DocumentType;
  selectedRowKeys: string[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
  canApproveReject: boolean;
  upload: (newDocuments: DocumentType[]) => Promise<void>;
  replace: (newDocument: DocumentType, archiveId: string) => Promise<void>;
  rename: (newName: string, documentId: string) => Promise<void>;
  archive: (documentId: string | string[]) => Promise<void>;
  restore: (documentId: string | string[]) => Promise<void>;
  print: (documentId: string | 'all') => Promise<void>;
  acceptReject: (
    status: 'accepted' | 'rejected',
    reason?: string,
  ) => Promise<void>;
  override: () => Promise<void>;
  editReason: (reason: string, sendNotification: boolean) => Promise<void>;
  setHistoryOpen: React.Dispatch<React.SetStateAction<string>>;
  historyOpen: string;
  setDocumentType: (
    newType: DocumentType['documentType'],
    id: string,
  ) => Promise<void>;
};

const DocumentsContext = React.createContext<DocumentsContextType>(null);

export const DocumentsContextProvider: React.FC = ({ children }) => {
  const { auth } = React.useContext(AuthContext);
  const history = useHistory();
  const { listingId, documentId } = useParams<Record<string, string>>();
  const [documents, setDocuments] = React.useState<DocumentType[]>([]);
  const [listing, setListing] = React.useState<ListingType>(null);

  const [historyOpen, setHistoryOpen] = React.useState<string>(null);
  const [loading, setLoading] = React.useState(true);
  const [currentTab, setCurrentTab] = React.useState<TabType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [appointmentsToCancel, setAppointmentsToCancel] = React.useState([]);

  const listingAgreement = React.useMemo(() => documents.find(({ documentType, isDeleted }) => !isDeleted && documentType === 'listingAgreement'), [documents]);

  const fetchDocuments = async () => {
    try {
      const { data } = await axios.get<ListingDocumentsDTO>(
        `/listing/${listingId}/documents`,
      );

      data.documents.sort((a, b) => {
        const priorityA = a.documentType === 'listingAgreement' ? 1 : 0;
        const priorityB = b.documentType === 'listingAgreement' ? 1 : 0;

        if (priorityA === priorityB) {
          if (moment(a.submissionTime).isBefore(moment(b.submissionTime))) {
            return -1;
          }
        }
        return priorityB - priorityA;
      });

      setDocuments(data.documents);
      setListing(data.listing);
    } catch (err) {
      errorReporter.error(err);
      if (err.response.data.type === 'FORBIDDEN_ACTION') {
        message.error(
          'You do not have permissions to view this page. Please contact support@brokerbay.com for more information.',
        );
      } else {
        message.error('Something went wrong. Please try again later.');
      }
      history.push(`/listing/${listingId}/view`);
    } finally {
      setLoading(false);
    }
  };

  const addHistoryEntry: AddHistoryEntryType = (id, entry) => {
    const ids = Array.isArray(id) ? id : [id];

    setDocuments(prev =>
      prev.map(doc => {
        if (ids.includes(doc.id)) {
          return {
            ...doc,
            history: [
              ...doc.history,
              {
                ...entry,
                updatedBy: auth.id,
                userName: `${auth.firstName} ${auth.lastName}`,
                date: new Date().toISOString(),
              },
            ],
          };
        }
        return doc;
      }),
    );
  };

  const upload: DocumentsContextType['upload'] = async newDocuments => {
    try {
      setLoading(true);
      const hasNewAgreement = newDocuments.some(
        ({ documentType }) => documentType === 'listingAgreement',
      );
      const archiveIds =
        hasNewAgreement && listingAgreement ? [listingAgreement.id] : [];
      await axios.post(`/listing/${listing.id}/documents`, {
        documents: newDocuments,
        archiveIds,
      });
      await fetchDocuments();
    } catch (err) {
      errorReporter.error(err);
      message.error('Something went wrong. Please try again later.');
      setLoading(false);
    }
  };

  const replace: DocumentsContextType['replace'] = async (
    newDocument,
    archiveId,
  ) => {
    try {
      setLoading(true);
      await axios.post(`/listing/${listing.id}/documents`, {
        documents: [newDocument],
        archiveIds: [archiveId],
      });
      await fetchDocuments();
      history.push(`/listing/${listing.id}/documents`);
    } catch (err) {
      errorReporter.error(err);
      message.error('Something went wrong. Please try again later.');
      setLoading(false);
    }
  };

  const rename = async (newName: string, id: string) => {
    let originalName: string;

    const oldDocuments = documents;
    const newDocuments = documents.map(document => {
      if (document.id === id) {
        originalName = document.name;
        return {
          ...document,
          name: newName,
        };
      }
      return document;
    });
    setDocuments(newDocuments);
    try {
      await axios.post(`/document/${id}/rename`, {
        name: newName,
      });
      addHistoryEntry(id, {
        action: 'renamed',
        nameFrom: originalName,
        nameTo: newName,
      });
    } catch (err) {
      errorReporter.error(err);
      setDocuments(oldDocuments);
      message.error('Something went wrong. Please try again later.');
    }
  };

  const archive = async (id: string | string[]) => {
    const ids = Array.isArray(id) ? id : [id];

    const agreementIds: string[] = [];

    const oldDocuments = documents;
    const newDocuments = documents.map(document => {
      if (document.documentType === 'listingAgreement') {
        agreementIds.push(document.id);
      } else if (ids.includes(document.id)) {
        return {
          ...document,
          isDeleted: true,
        };
      }
      return document;
    });
    setDocuments(newDocuments);
    setSelectedRowKeys([]);
    const filteredIds = ids.filter(docId => !agreementIds.includes(docId));
    try {
      await axios.post(`/listing/${listingId}/documents/archive`, {
        documentIds: filteredIds,
        archive: true,
      });
      addHistoryEntry(filteredIds, {
        action: 'archived',
      });
    } catch (err) {
      errorReporter.error(err);
      setDocuments(oldDocuments);
      message.error('Something went wrong. Please try again later.');
    }
  };

  const restore = async (id: string | string[]) => {
    const ids = Array.isArray(id) ? id : [id];

    let isRestoringListingAgreement: boolean;
    let previousListingAgreement: string;

    const oldDocuments = documents;
    let newDocuments = documents.map(document => {
      if (ids.includes(document.id)) {
        if (document.documentType === 'listingAgreement') {
          isRestoringListingAgreement = true;
        }
        return {
          ...document,
          isDeleted: false,
        };
      }
      if (document.documentType === 'listingAgreement' && !document.isDeleted) {
        previousListingAgreement = document.id;
      }
      return document;
    });

    if (isRestoringListingAgreement) {
      newDocuments = newDocuments.map(document => {
        if (document.id === previousListingAgreement) {
          return {
            ...document,
            isDeleted: true,
          };
        }
        return document;
      });
    }

    setDocuments(newDocuments);
    setSelectedRowKeys([]);
    try {
      await axios.post(`/listing/${listingId}/documents/archive`, {
        documentIds: ids,
        archive: false,
      });
      addHistoryEntry(ids, {
        action: 'restored',
      });
      if (isRestoringListingAgreement) {
        addHistoryEntry(previousListingAgreement, {
          action: 'archived',
        });
        if (
          !auth?.isAdmin &&
          listing &&
          listing.documentStatus !== 'uploaded'
        ) {
          setListing(prev => ({ ...prev, documentStatus: 'pending' }));
        }
      }
    } catch (err) {
      errorReporter.error(err);
      setDocuments(oldDocuments);
      message.error('Something went wrong. Please try again later.');
    }
  };

  const print = async (id: string | 'all') => {
    setLoading(true);
    try {
      await axios.post(`/listing/${listingId}/documents/print`, {
        documentIds: [id],
      });
      const { url, fileType } = documents.find(doc => doc.id === id);
      printJS({
        printable: url,
        type: fileType === 'application/pdf' ? 'pdf' : 'image',
        onLoadingEnd: () => setLoading(false),
        onError: () =>
          message.error('Something went wrong. Please try again later.'),
      });
      addHistoryEntry(id, {
        action: 'printed',
      });
    } catch (err) {
      errorReporter.error(err);
      message.error('Something went wrong. Please try again later.');
      setLoading(false);
    }
  };

  const setDocumentType = async (
    newType: DocumentType['documentType'],
    id: string,
  ) => {
    let typeFrom: DocumentType['documentType'];
    let agreementId: string;
    const oldDocuments = documents;
    const newDocuments = documents.map(document => {
      if (document.id === id) {
        typeFrom = document.documentType;
        return {
          ...document,
          documentType: newType,
        };
      }
      // Archives Listing Agreement if setting document to that type
      if (
        newType === 'listingAgreement' &&
        document.documentType === 'listingAgreement'
      ) {
        agreementId = document.id;
        return {
          ...document,
          isDeleted: true,
        };
      }
      return document;
    });

    const oldListing = listing;
    if (
      newType === 'listingAgreement' &&
      !auth?.isAdmin &&
      listing &&
      listing.documentStatus !== 'uploaded'
    ) {
      setListing(prev => ({
        ...prev,
        documentStatus: 'pending',
      }));
    }

    setDocuments(newDocuments);
    try {
      await axios.post(`/document/${id}/change_type`, {
        documentType: newType,
      });
      addHistoryEntry(id, {
        action: 'type_changed',
        typeFrom,
        typeTo: newType,
      });
      if (agreementId) {
        addHistoryEntry(agreementId, {
          action: 'archived',
        });
      }
    } catch (err) {
      errorReporter.error(err);
      setDocuments(oldDocuments);
      setListing(oldListing);
      message.error('Something went wrong. Please try again later.');
    }
  };
  const successMessage = (pendingVisibility: string) => {
    let baseMessage = 'Your Listing has been';
    if (pendingVisibility === 'draft') return `${baseMessage} saved as a draft`;

    baseMessage += ' created and shared to';
    if (pendingVisibility === 'brokerage') {
      return `${baseMessage} your brokerage.`;
    }
    if (pendingVisibility === 'network') {
      return `${baseMessage} the BrokerBay Exclusive Network.`;
    }
    errorReporter.error(
      `Could not generate a document action success message for listing ${listing.id} with pendingVisibility ${pendingVisibility}`,
    );
  };

  const acceptReject: DocumentsContextType['acceptReject'] = async (
    status,
    reason,
  ) => {
    setLoading(true);
    try {
      await axios.post(`/listing/${listing.id}/documents/process`, {
        status,
        reason,
      });
      history.push(`/admin/dashboard?requestType=exclusives`);

      message.success(
        status === 'accepted'
          ? successMessage(listing.pendingVisibility)
          : 'Document rejected',
      );
    } catch (err) {
      errorReporter.error(err);
      message.error('Something went wrong. Please try again later.');
      setLoading(false);
    }
  };

  const override: DocumentsContextType['override'] = async () => {
    const prevListing = listing;
    setListing(prev => ({ ...prev, documentStatus: 'pending' }));
    try {
      await axios.post(`/listing/${listing.id}/documents/override`);

      addHistoryEntry(listingAgreement.id, {
        action: 'overridden',
      });

      history.push(`/listing/${listing.id}/documents`);
    } catch (err) {
      errorReporter.error(err);
      message.error('Something went wrong. Please try again later.');
      setListing(prevListing);
    }
  };

  const editReason: DocumentsContextType['editReason'] = async (
    reason,
    sendNotification,
  ) => {
    const prevDocuments = documents;
    const newDocuments = documents.map(doc => {
      if (doc === listingAgreement) {
        return {
          ...doc,
          reason,
        };
      }
      return doc;
    });
    setDocuments(newDocuments);

    try {
      await axios.post(`/document/${listingAgreement.id}/edit_reason`, {
        reason,
        sendNotification,
      });
      history.push(`/listing/${listing.id}/documents`);
    } catch (err) {
      errorReporter.error(err);
      message.error('Something went wrong. Please try again later.');
      setDocuments(prevDocuments);
    }
  };

  const isAuthorized = React.useMemo(() => {
    if (!auth) return null;
    if (auth.role === 'AGENT' && documentId === 'all') {
      return false;
    }

    if (documentId && documentId !== 'all') {
      return documents.some(({ id }) => id === documentId);
    }

    return true;
  }, [documents, documentId, auth, listing]);

  const canApproveReject = React.useMemo(() => {
    if (!auth || !listing) return false;
    return isAuthForRole({ auth, locations: listing.locations }) as boolean;
  }, [auth, listing]);

  const documentsToView = React.useMemo(() => {
    if (!documentId) return [];

    if (documentId !== 'all') {
      const doc = documents.find(({ id }) => id === documentId);
      return [doc];
    }

    return documents.filter(({ isDeleted }) => !isDeleted);
  }, [documents, documentId]);

  const closeCancelAppointmentsModal = React.useCallback(() => {
    setAppointmentsToCancel([]);
    history.push(`/listing/${listing.id}/documents`);
  }, [listing, history, setAppointmentsToCancel]);

  React.useEffect(() => {
    fetchDocuments();
  }, []);

  React.useEffect(() => {
    if (!isAuthorized) {
      if (auth && listing) history.replace(`/listing/${listingId}/view`);
    }
  }, [auth, listing, isAuthorized, history, listingId]);

  if (!listing) return null;

  return (
    <DocumentsContext.Provider
      value={{
        loading,
        currentTab,
        setCurrentTab,
        documents,
        documentsToView,
        listing,
        listingAgreement,
        selectedRowKeys,
        setSelectedRowKeys,
        canApproveReject,
        upload,
        replace,
        rename,
        archive,
        restore,
        print,
        acceptReject,
        override,
        editReason,
        setHistoryOpen,
        historyOpen,
        setDocumentType,
      }}
    >
      <ClickWrapper $loading={loading}>
        {loading && <Spin size="large" center />}
        {children}
        {listing && (
          <ModalComponents.CancelAppointmentsModal
            listingId={listing.id}
            appointments={appointmentsToCancel}
            closeModal={closeCancelAppointmentsModal}
          />
        )}
      </ClickWrapper>
    </DocumentsContext.Provider>
  );
};

const ClickWrapper = styled.div<{ $loading: boolean }>`
  position: relative;
  ${Spin} {
    ${({ $loading }) =>
      $loading &&
      `
    position: absolute;
    top: 0;
    left: 0;
    background: white;
    opacity: 0.8;
    z-index: 1;
  `};
  }
`;

export default DocumentsContext;
